import React from "react";
import { Avatar, Box, Link, Typography } from "@mui/material";
import ConfirmationModal from "../ConfirmationModal";
import { COLORS } from "./colors";

export const formatDateTime = (isoDate) => {
  const date = new Date(isoDate);
  const day = String(date.getUTCDate()).padStart(2, "0");
  const month = String(date.getUTCMonth() + 1).padStart(2, "0");
  const hours = String(date.getUTCHours()).padStart(2, "0");
  const minutes = String(date.getUTCMinutes()).padStart(2, "0");
  return `${day}-${month}-${date.getUTCFullYear()}, ${hours}:${minutes}`;
};

// Shared read-only display for a top-level comment + its single reply (if any), with the
// Reply/Edit/Delete actions each caller wires up — used by both the live-tip comment preview
// and the full comments lightbox so the two stay visually identical.
const CommentThread = ({ comment, onReply, onDelete, onEditReply, editingReplyId, idPrefix }) => (
  <Box id={idPrefix ? `${idPrefix}-${comment._id}` : undefined}>
    <Box sx={{ display: "flex", gap: 1.5, mb: 1 }}>
      <Avatar sx={{ bgcolor: "#5C6BC0", width: 32, height: 32, fontSize: 14 }}>
        {comment.authorName.charAt(0)}
      </Avatar>
      <Box sx={{ flexGrow: 1 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {comment.authorName}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {formatDateTime(comment.createdAt)}
          </Typography>
          <Box sx={{ flexGrow: 1 }} />
          {onReply && !comment.reply && (
            <Link component="button" variant="body2" underline="none" sx={{ color: COLORS.link }} onClick={() => onReply(comment)}>
              Reply
            </Link>
          )}
          <ConfirmationModal
            title="Delete comment?"
            description="Are you sure you want to permanently delete this comment? This will permanently delete the comment and all replies beneath it. This action cannot be undone."
            confirmLabel="Delete comment"
            cancelLabel="Cancel"
            showCloseButton
            destructive
            onConfirm={() => onDelete(comment._id)}
            renderTrigger={({ openModal }) => (
              <Link component="button" variant="body2" underline="none" sx={{ color: COLORS.destructive }} onClick={openModal}>
                Delete
              </Link>
            )}
          >
            <Box
              sx={{
                display: "flex",
                gap: 1.5,
                p: 1.5,
                mt: 2,
                border: "1px solid",
                borderColor: COLORS.border,
                borderRadius: 1.5,
              }}
            >
              <Avatar sx={{ bgcolor: "#5C6BC0", width: 32, height: 32, fontSize: 14 }}>
                {comment.authorName.charAt(0)}
              </Avatar>
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {comment.authorName}
                </Typography>
                <Typography variant="body2">{comment.text}</Typography>
              </Box>
            </Box>
          </ConfirmationModal>
        </Box>
        <Typography variant="body2">{comment.text}</Typography>
      </Box>
    </Box>

    {comment.reply && editingReplyId !== comment.reply._id && (
      <Box sx={{ display: "flex", gap: 1.5, ml: 4, mb: 1.5 }}>
        <Avatar sx={{ bgcolor: "#2E7D32", width: 24, height: 24, fontSize: 12 }}>
          {comment.reply.authorName.charAt(0)}
        </Avatar>
        <Box sx={{ flexGrow: 1 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {comment.reply.authorName}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {formatDateTime(comment.reply.createdAt)}
            </Typography>
            <Box sx={{ flexGrow: 1 }} />
            {onEditReply && (
              <Link component="button" variant="body2" underline="none" sx={{ color: COLORS.link }} onClick={() => onEditReply(comment.reply)}>
                Edit
              </Link>
            )}
            <ConfirmationModal
              title="Delete this reply?"
              description="This can't be undone."
              confirmLabel="Delete"
              cancelLabel="Cancel"
              destructive
              onConfirm={() => onDelete(comment.reply._id)}
              renderTrigger={({ openModal }) => (
                <Link component="button" variant="body2" underline="none" sx={{ color: COLORS.destructive }} onClick={openModal}>
                  Delete
                </Link>
              )}
            />
          </Box>
          <Typography variant="body2">{comment.reply.text}</Typography>
        </Box>
      </Box>
    )}
  </Box>
);

export default CommentThread;
